import React, { MouseEventHandler, useEffect, useRef, useState } from 'react'
import Navigation from '../partials/Navigation'
import SwitchImg from '../images/switch.svg'
import PlacesAutocomplete, { geocodeByAddress, geocodeByPlaceId, getLatLng } from 'react-places-autocomplete'
import PassengersAndLuggage from '../partials/booking/PassengersAndLuggage'
import PickupDate from '../partials/booking/PickupDate'
import Alert from '../components/Alert'
import { useHistory, useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../store'
import { getAirportName, TravelPayoutAutocompleteResult } from '../hooks/useAirportLookup'
import {
  logError,
  recordDisclaimers,
  selectTransfer,
  setDirection,
  swapDirection,
  updatePickupDate,
  updateTransferSearchQuery,
} from '../store/slices/app'
import {
  Address,
  isValidationFailedResponse,
  LuggageRequirements,
  OversizeLuggageRequirements,
  PassengerRequirements,
  TransferDirection,
} from '../types'
import useTransfers from '../hooks/useTransfers'
import { TransferSearchQuery, TransferSearchResult, TransferSearchResultCollection } from '../hooks/useTransfers/types'
import {
  CarIcon,
  TransferSearchResultGroupListItem,
  TransferSearchResultList,
} from '../components/TransferSearchResults'
import Placeholder from '../components/TransferSearchResults/Placeholder'
import {
  PageURLs,
  pluckLuggageRequirements,
  pluckOversizedLuggageRequirements,
  pluckPassengerRequirements,
} from '../common'
import { Props as TransferSearchResultGroupProps } from '../components/TransferSearchResults/TransferSearchResultGroupListItem'
import { formatISO, isValid as isValidDate, parseISO } from 'date-fns'
import Page from '../components/Page'
import NavigationArrow from '../components/NavigationArrow'
import Form from '../components/Form'
import { isAxiosError } from 'axios'
import Drawer from '../components/Drawer'
import Icons from '../components/Icons'
import useDisclaimers from '../hooks/useDisclaimers'
import AirportLookup from '../components/AirportLookup'

const BookNow = () => {
  const dispatch = useDispatch()
  const { search } = useLocation()
  const transfers = useTransfers()
  const disclaimersApi = useDisclaimers()
  const { transferBookingIds } = useSelector((state: RootState) => state.app)
  const transferSearchQuery: TransferSearchQuery = useSelector((state: RootState) => state.app.transferSearchQuery)
  const { adults, children, infants, direction } = transferSearchQuery
  const isToAirport = direction === TransferDirection.ToAirport

  const [showGroupResults, setShowGroupResults] = useState(false)
  type PageState = 'initial' | 'loading' | 'results' | 'no-results' | 'error'
  const [pageState, setPageState] = useState<PageState>('initial')
  const [error, setError] = useState('') // TODO: Delete? Use an error boundary?
  const [validationErrors, setValidationErrors] = useState({})
  const [transferSearchResults, setTransferSearchResults] = useState<TransferSearchResultCollection[]>([])
  const formattedAddress = transferSearchQuery.address_formatted
  const $flightNumber = useRef<HTMLDivElement>(null)
  const $connectingAirport = useRef<HTMLDivElement>(null)

  const isLoading = pageState === 'loading'
  const totalPassengers = adults + children + infants

  const onSearchForTransfers: MouseEventHandler = (e) => {
    e.preventDefault()
    if (validateFlightNumber()) {
      fetchTransfers()
    }
  }

  const fetchTransfers = async () => {
    if (pageState === 'loading') return

    setError('')
    setValidationErrors({})

    if (
      totalPassengers <= 0 ||
      !transferSearchQuery.airport_code ||
      !transferSearchQuery.connecting_airport_code ||
      !formattedAddress ||
      !isValidDate(parseISO(transferSearchQuery.pickup_datetime))
    ) {
      return
    }

    setPageState('loading')

    setTransferSearchResults([])

    try {
      const response = await transfers.search(transferSearchQuery)

      setTransferSearchResults(response.data)
      setPageState(response.data.some((x) => x.results.length > 0) ? 'results' : 'no-results')
    } catch (error) {
      dispatch(logError(error))
      if (isValidationFailedResponse(error)) {
        setValidationErrors({ ...error.response.data.data.params })
        const count = Object.values(error.response.data.data.params).length
        setError(
          count === 1
            ? 'Sorry there was an error processing your request.'
            : `Sorry there were ${count} errors processing your request`
        )
      } else if (isAxiosError(error) && Array.isArray(error.response?.data.errors)) {
        setError(error.response!.data.errors[0])
      } else {
        setError('An unknown error occurred.')
      }
      setPageState('error')
      console.error(error)
    }
  }

  const toTransferSearchResultGroupProps = (
    x: TransferSearchResultCollection
  ): Omit<TransferSearchResultGroupProps, 'loading' | 'onSelected'> => {
    const { id, title, results, min_price, max_price } = x

    return {
      icon: id,
      title: title,
      minPrice: min_price,
      maxPrice: max_price,
      results,
    }
  }

  useEffect(() => {
    updateTransferQueryFromSearchParams([
      'airport_name',
      'airport_code',
      'airport_country',
      'airport_lat',
      'airport_lng',
    ])

    updateTransferQueryFromSearchParams([
      'connecting_airport_name',
      'connecting_airport_code',
      'connecting_airport_country',
      'connecting_airport_lat',
      'connecting_airport_lng',
    ])

    const params = new URLSearchParams(search)
    const addressPlaceId: string = params.get('address_id') || ''
    if (addressPlaceId) {
      updateAddressFromPlaceId(addressPlaceId)
    }
  }, [])

  const updateTransferQueryFromSearchParams = (keys: string[], minimumRequiredKeys: string[] = []) => {
    const params = new URLSearchParams(search)
    const payload = keys.reduce((prev, key) => ({ ...prev, [key]: params.get(key) }), {})
    const expectedKeys = minimumRequiredKeys.length > 0 ? minimumRequiredKeys : keys

    for (const key of expectedKeys) {
      if (!payload[key]) {
        return
      }
    }

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    dispatch(updateTransferSearchQuery(payload))

    return payload
  }
  useEffect(() => {
    const urlParams = new URLSearchParams(search)

    const direction = urlParams.get('direction')

    if (direction === TransferDirection.ToAddress || direction === TransferDirection.ToAirport) {
      dispatch(setDirection(direction))
    }
  }, [])

  ///
  /// Address Lookup
  ///
  const [placesLookupQuery, setPlacesLookupQuery] = useState(formattedAddress)
  const searchOptions = {
    // Create a bounding box with sides ~100km away from the center point (~200km radius)
    bounds:
      transferSearchQuery.airport_lat && transferSearchQuery.airport_lng
        ? {
            north: parseFloat(transferSearchQuery.airport_lat as string) + 1,
            south: parseFloat(transferSearchQuery.airport_lat as string) - 1,
            east: parseFloat(transferSearchQuery.airport_lng as string) + 1,
            west: parseFloat(transferSearchQuery.airport_lng as string) - 1,
          }
        : undefined,
    strictBounds: false,
    componentRestrictions: {
      country: transferSearchQuery.airport_country || null,
    },
  }

  const getAddressComponent = (result: google.maps.GeocoderResult, key: string) => {
    for (const part of result.address_components) {
      if (part.types.includes(key)) {
        return part.short_name
      }
    }

    return ''
  }

  const fakeMap = document.createElement('div')
  const getGooglePlaceById = async (placeId: string): Promise<google.maps.places.PlaceResult | null> => {
    const fields = ['name', 'address_components', 'place_id', 'geometry']
    const service = new google.maps.places.PlacesService(fakeMap)

    return new Promise((resolve, reject) => {
      const request = { placeId, fields }
      service.getDetails(request, (results, status) => {
        if (status !== google.maps.places.PlacesServiceStatus.OK) {
          reject(status)
        }

        resolve(results)
      })
    })
  }

  const geocodeResultToAddress = async (result: google.maps.GeocoderResult): Promise<Address> => {
    const place = await getGooglePlaceById(result.place_id)
    const point = await getLatLng(result)

    return {
      address_place_id: result.place_id,
      address_components: result.address_components,
      address_name: place?.name || result.formatted_address,
      address_formatted: result.formatted_address,
      address_country: getAddressComponent(result, 'country'),
      address_lat: point.lat,
      address_lng: point.lng,
    }
  }

  const updateAddressFromPlace = async (result: google.maps.GeocoderResult) => {
    try {
      const address = await geocodeResultToAddress(result)

      setPlacesLookupQuery(result.formatted_address)
      dispatch(updateTransferSearchQuery(address))
    } catch (error) {
      dispatch(logError(error))
      console.error('Error', error)
    }
  }

  const updateAddressFromPlaceId = async (placeId: string) => {
    try {
      await updateAddressFromPlace((await geocodeByPlaceId(placeId))[0])
    } catch (error) {
      dispatch(logError(error))
      console.error('Error', error)
    }
  }

  const handlePlacesAutocompleteSelected = async (addressAutocomplete: string) => {
    try {
      await updateAddressFromPlace((await geocodeByAddress(addressAutocomplete))[0])
    } catch (error) {
      dispatch(logError(error))
      console.error('Error', error)
    }
  }

  ///
  /// Airport Lookup
  ///
  const handleAirportSelected = (result: TravelPayoutAutocompleteResult) => {
    dispatch(
      updateTransferSearchQuery({
        airport_name: getAirportName(result),
        airport_code: result.code,
        airport_country: result.country_code,
        airport_lat: result.coordinates.lat,
        airport_lng: result.coordinates.lon,
      })
    )
  }

  const handleConnectingAirportSelected = (result: TravelPayoutAutocompleteResult) => {
    dispatch(
      updateTransferSearchQuery({
        connecting_airport_name: getAirportName(result),
        connecting_airport_code: result.code,
        connecting_airport_country: result.country_code,
        connecting_airport_lat: result.coordinates.lat,
        connecting_airport_lng: result.coordinates.lon,
      })
    )
  }

  ///
  /// Booking
  ///
  const history = useHistory()

  useEffect(() => {
    if (transferSearchQuery.flight_number.trim().length > 0) {
      setValidationErrors({ ...validationErrors, flight_number: undefined })
    }
  }, [transferSearchQuery.flight_number])

  const [selectedGroup, setSelectedGroup] = useState<{
    icon: CarIcon
    title: string
    results: TransferSearchResult[]
  } | null>(null)
  const onGroupSelected = (icon: CarIcon, title: string, results: TransferSearchResult[]) => {
    setSelectedGroup({ icon, title, results })
    setShowGroupResults(true)
  }

  const validateFlightNumber = () => {
    const flightNumber = transferSearchQuery.flight_number.trim()
    const validation = /^[A-Za-z]{1,}\w+/

    if (flightNumber.length === 0) {
      setValidationErrors({ ...validationErrors, flight_number: 'Please enter the flight number.' })
      $flightNumber.current?.scrollIntoView({ block: 'start', inline: 'nearest', behavior: 'smooth' })
      return false
    }

    if (!validation.test(flightNumber)) {
      setValidationErrors({ ...validationErrors, flight_number: 'Please enter a valid flight number.' })
      $flightNumber.current?.scrollIntoView({ block: 'start', inline: 'nearest', behavior: 'smooth' })

      return false
    }

    return true
  }

  const onTransferSelected = async (result: TransferSearchResult) => {
    if (validateFlightNumber()) {
      if (result.has_disclaimer) {
        const {
          data: { disclaimers },
        } = await disclaimersApi.show(result)

        dispatch(recordDisclaimers({ bookingId: result.booking_id, disclaimers }))
      }

      dispatch(selectTransfer(result))
      history.push(PageURLs.CompleteBooking)
    } else {
      setShowGroupResults(false)
    }
  }

  ///
  /// UI
  ///

  const switchDirectionComponent = (
    <div className="col-1 px-0 d-flex justify-content-center align-items-center">
      <button
        type="button"
        className="btn btn-link text-secondary px-0"
        onClick={() => {
          dispatch(swapDirection())
        }}
      >
        <img src={SwitchImg} className="img-fluid" alt="Switch Icon" />
      </button>
    </div>
  )

  const addressSearch = (
    <>
      <div
        className={`booking-input-single booking-input-start mb-4 position-relative ${
          isToAirport ? 'first' : 'second'
        }`}
      >
        <div className="row">
          <div className={isToAirport ? 'col-12' : 'col-11'}>
            <PlacesAutocomplete
              value={placesLookupQuery}
              searchOptions={searchOptions}
              onChange={(address: string) => {
                setPlacesLookupQuery(address)
              }}
              onSelect={handlePlacesAutocompleteSelected}
            >
              {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
                <div className="autocomplete-input-wrap position-relative">
                  {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
                  {/* @ts-ignore */}
                  <input
                    {...getInputProps({
                      placeholder: 'Start typing address',
                      className: 'form-control',
                    })}
                  />
                  <div className="autocomplete-dropdown-container position-absolute w-100 d-inline-block bg-white">
                    {loading && <div>Loading...</div>}
                    {suggestions.map((suggestion) => {
                      const className = suggestion.active
                        ? 'suggestion-item--active px-4 py-2'
                        : 'suggestion-item px-4 py-2'
                      return (
                        <div
                          {...getSuggestionItemProps(suggestion, {
                            className,
                          })}
                          key={suggestion.placeId}
                        >
                          <span>{suggestion.description}</span>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}
            </PlacesAutocomplete>
          </div>
          {!isToAirport && switchDirectionComponent}
        </div>
      </div>
    </>
  )

  return (
    <Page className="bg-blue-white">
      <Navigation />

      <div className="container pt-xl-6">
        {transferBookingIds.length > 0 && (
          <NavigationArrow compact onClick={() => history.push(PageURLs.CompleteBooking)}>
            Back to transfers
          </NavigationArrow>
        )}
        <h2 className="mb-5">Book a transfer</h2>
      </div>
      <div className="container-lg">
        <div className="row">
          <div className="col-12 col-lg-5">
            <div className="container">
              <div className="row">
                <div className="booking-inputs-wrap col-12 px-0 px-lg-3">
                  <div className="booking-inputs">
                    {!!error && <Alert type="danger" msg={error} />}
                    <div className={`d-flex ${isToAirport ? 'flex-column' : 'flex-column-reverse'}`}>
                      {addressSearch}

                      <div
                        className={`booking-input-single booking-input-end booking-input-airport mb-4 position-relative ${
                          isToAirport ? 'second' : 'first'
                        }`}
                      >
                        <div className="row">
                          <AirportLookup
                            initialAirportName={transferSearchQuery.airport_name}
                            className={isToAirport ? 'col-11' : 'col-12'}
                            handleAirportSelected={handleAirportSelected}
                          />
                          {isToAirport && switchDirectionComponent}
                        </div>
                      </div>
                    </div>

                    <PassengersAndLuggage
                      passengers={pluckPassengerRequirements(transferSearchQuery)}
                      luggage={pluckLuggageRequirements(transferSearchQuery)}
                      oversized={pluckOversizedLuggageRequirements(transferSearchQuery)}
                      onChange={(
                        requirements: PassengerRequirements & LuggageRequirements & OversizeLuggageRequirements
                      ) => {
                        dispatch(updateTransferSearchQuery(requirements))
                      }}
                    />
                    <PickupDate
                      value={transferSearchQuery.pickup_datetime}
                      onChange={(date: Date) => {
                        dispatch(updatePickupDate(formatISO(date)))
                      }}
                    >
                      <Form.ValidationError message={validationErrors['pickup_datetime']} />
                    </PickupDate>

                    <div className="mb-4 position-relative">
                      <div className="passengers-left-icon position-absolute">
                        <span className="icon-star"></span>
                      </div>
                      <div className="form-group" ref={$flightNumber}>
                        <label htmlFor="flight_number" className="sr-only">
                          Flight number
                        </label>
                        <input
                          type="text"
                          className="form-control form-control-lg"
                          id="flight_number"
                          name="flight_number"
                          placeholder="Flight number"
                          onChange={(event) =>
                            dispatch(updateTransferSearchQuery({ flight_number: event.target.value }))
                          }
                          value={transferSearchQuery.flight_number}
                          required
                        />
                        <Form.ValidationError message={validationErrors['flight_number']} />
                      </div>
                    </div>

                    <div className="mb-4 position-relative">
                      <div className="passengers-left-icon position-absolute">
                        <span className="icon-plane"></span>
                      </div>
                      <div className="form-group" ref={$connectingAirport}>
                        <AirportLookup
                          initialAirportName={transferSearchQuery.connecting_airport_name || ''}
                          handleAirportSelected={handleConnectingAirportSelected}
                          placeholder={isToAirport ? "Airport you're flying to?" : "Airport you're flying from?"}
                        />
                        <Form.ValidationError message={validationErrors['connecting_airport']} />
                      </div>
                    </div>

                    <div className="booking-input-single mb-4">
                      <button className="btn btn-primary w-100" onClick={onSearchForTransfers} disabled={isLoading}>
                        Find your transfer
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-12 offset-lg-2 col-lg-5 transfer-search-result-group-wrapper bg-white">
            <div className="container">
              <div className="row">
                <div className="col-12">
                  {pageState === 'initial' && (
                    <>
                      <div className="transfer-search-empty-state">
                        <Icons.CarLuxury />
                        <h3>Pick your starting points to see possible transfers</h3>
                      </div>
                    </>
                  )}

                  {pageState === 'no-results' && (
                    <>
                      <div className="transfer-search-empty-state">
                        <Icons.CarShared />
                        <h3>Sorry, we could not find any matches</h3>
                      </div>
                    </>
                  )}

                  {pageState === 'error' && (
                    <>
                      <div className="transfer-search-empty-state">
                        <Icons.CarEconomy />
                        <h3>Sorry an error occurred.</h3>
                      </div>
                    </>
                  )}

                  {isLoading && (
                    <>
                      <Placeholder />
                    </>
                  )}

                  {pageState === 'results' && (
                    <>
                      <p className="text-center">Pick your preferred option</p>
                      {transferSearchResults
                        .map(toTransferSearchResultGroupProps)
                        .filter((x) => x.results.length > 0)
                        .map((group, idx) => (
                          <TransferSearchResultGroupListItem
                            key={`transfer-search-results-group-${idx}`}
                            onSelected={onGroupSelected}
                            {...group}
                          />
                        ))}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Drawer visible={showGroupResults} onBackgroundClick={() => setShowGroupResults(false)}>
        {selectedGroup && (
          <div className="px-4 pb-6">
            <NavigationArrow compact onClick={() => setShowGroupResults(false)} />

            <TransferSearchResultList {...selectedGroup} onSelected={onTransferSelected} />
          </div>
        )}
      </Drawer>
    </Page>
  )
}

export default BookNow
